// src/services/messageService.ts
// import { apiClient } from './apiClient'; // Your configured API client
import { Message } from '../models/Message'; // Message type/interface
import axios from 'axios';

// Local storage key manager for last message tracking
const LAST_MESSAGE_ID_PREFIX = 'lastMessageId';

// Interface for API response
interface MessageResponse {
  items: Message[];
  totalCount: number;
}

// Interface for function parameters
interface FetchMessagesParams {
  groupId: string;
  lastMessageId?: string;
  pageSize?: number;
}

/**
 * Gets the last seen message ID for a specific group from localStorage
 * @param groupId - ID of the chat group/channel
 * @returns Last stored message ID or null if none exists
 */
const getLastMessageId = (groupId: string): string | null => {
  return localStorage.getItem(`${LAST_MESSAGE_ID_PREFIX}_${groupId}`);
};

/**
 * Stores the last seen message ID for a specific group in localStorage
 * @param groupId - ID of the chat group/channel
 * @param messageId - ID of the last message to store
 */
const setLastMessageId = (groupId: string, messageId: string): void => {
  localStorage.setItem(`${LAST_MESSAGE_ID_PREFIX}_${groupId}`, messageId);
};

/**
 * Fetches new messages from the server based on last known message
 * @param params - Contains groupId and optional pagination parameters
 * @param currentMessages - Current messages in client state
 * @returns Promise resolving to combined messages array
 * @throws Error when failed to fetch messages
 */
export const fetchNewMessages = async (
  params: FetchMessagesParams,
  currentMessages: Message[]
): Promise<Message[]> => {
  try {
    // Get last message ID from storage or existing messages
    const lastMessageId = params.lastMessageId || 
      getLastMessageId(params.groupId) ||
      currentMessages[currentMessages.length - 1]?.id;

    // API call configuration
    const response = await axios.get<MessageResponse>(
      '/api/services/app/Message/GetMessagesAfter',
      {
        params: {
          groupId: params.groupId,
          lastMessageId,
          maxResultCount: params.pageSize || 50
        }
      }
    );

    if (response.data.items.length > 0) {
      // Update local storage with new last message ID
      const newLastId = response.data.items[response.data.items.length - 1].id;
      setLastMessageId(params.groupId, newLastId);

      // Return combined messages (deduplicated)
      return [...currentMessages, ...response.data.items].filter(
        (message, index, self) =>
          index === self.findIndex(m => m.id === message.id)
      );
    }

    return currentMessages;
  } catch (error) {
    console.error('Message fetch failed:', error);
    throw new Error(
      'Failed to retrieve new messages. Please check your connection.'
    );
  }
};

/**
 * Posts a new message to the server
 * @param message - Message object to send
 * @returns Promise resolving to the created Message
 * @throws Error when message send fails
 */
export const sendMessage = async (message: Omit<Message, 'id' | 'timestamp'>): Promise<Message> => {
  try {
    const response = await axios.post<Message>(
      '/api/services/app/Message/Create',
      message
    );
    return response.data;
  } catch (error) {
    console.error('Message send failed:', error);
    throw new Error(
      'Failed to send message. Please try again.'
    );
  }
};